<template>
  <div class="address-sub-form">
    <!-- ROW 1: City and ZIP -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
      <!-- City -->
      <div>
        <label for="city" class="form-label w-full flex flex-col sm:flex-row">
          {{ $t('address.city') }}
        </label>
        <div class="relative">
          <input
            id="city"
            v-model.trim="validate.city.$model"
            type="text"
            class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13 w-full border"
            :class="{ 'border-theme-6 border-red-500': validate.city.$error }"
            placeholder="Poprad"
            @input="handleInput('city')"
            autocomplete="off"
          />
          <ul
            v-if="activeField === 'city' && suggestions.length"
            class="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded shadow-lg max-h-48 overflow-y-auto"
          >
            <li
              v-for="(item, index) in suggestions"
              :key="index"
              class="p-2 hover:bg-gray-100 cursor-pointer border-b last:border-0"
              @click="selectAddress(item)"
            >
              {{ item.display_name }}
            </li>
          </ul>
        </div>
        <div v-if="validate.city.$error" class="text-theme-6 text-red-500 mt-2 text-sm">
          {{ $t('form.validation.requiredField') }}
        </div>
      </div>

      <!-- ZIP -->
      <div>
        <label for="zip" class="form-label w-full flex flex-col sm:flex-row">
          {{ $t('address.zip') }}
        </label>
        <div class="relative">
          <input
            id="zip"
            v-model.trim="validate.zip.$model"
            type="text"
            class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13 w-full border"
            :class="{ 'border-theme-6 border-red-500': validate.zip.$error }"
            placeholder="040 11"
            @input="handleInput('postalcode')"
            autocomplete="off"
          />
          <ul
            v-if="activeField === 'postalcode' && suggestions.length"
            class="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded shadow-lg max-h-48 overflow-y-auto"
          >
            <li
              v-for="(item, index) in suggestions"
              :key="index"
              class="p-2 hover:bg-gray-100 cursor-pointer border-b last:border-0"
              @click="selectAddress(item)"
            >
              {{ item.display_name }}
            </li>
          </ul>
        </div>
        <div v-if="validate.zip.$error" class="text-theme-6 text-red-500 mt-2 text-sm">
          {{ $t('form.validation.requiredField') }}, {{ $t('form.validation.zipValidator') }}
        </div>
      </div>
    </div>

    <!-- ROW 2: Street and Number -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
      <!-- Street -->
      <div class="sm:col-span-2">
        <label for="street" class="form-label w-full flex flex-col sm:flex-row">
          {{ $t('address.street') }}
        </label>
        <div class="relative">
          <input
            id="street"
            v-model.trim="validate.street.$model"
            type="text"
            class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13 w-full border"
            :class="{ 'border-theme-6 border-red-500': validate.street.$error }"
            placeholder="Hlavná"
            @input="handleInput('street')"
            autocomplete="off"
          />
          <ul
            v-if="activeField === 'street' && suggestions.length"
            class="absolute z-50 w-full bg-white border border-gray-200 mt-1 rounded shadow-lg max-h-48 overflow-y-auto"
          >
            <li
              v-for="(item, index) in suggestions"
              :key="index"
              class="p-2 hover:bg-gray-100 cursor-pointer border-b last:border-0"
              @click="selectAddress(item)"
            >
              {{ item.display_name }}
            </li>
          </ul>
        </div>
        <div v-if="validate.street.$error" class="text-theme-6 text-red-500 mt-2 text-sm">
          {{ $t('form.validation.requiredField') }}
        </div>
      </div>

      <!-- Street Number -->
      <div>
        <label for="streetNumber" class="form-label w-full flex flex-col sm:flex-row">
          {{ $t('address.streetNumber') }}
        </label>
        <input
          id="streetNumber"
          v-model.trim="validate.streetNumber.$model"
          type="text"
          class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13 w-full border"
          :class="{ 'border-theme-6 border-red-500': validate.streetNumber.$error }"
          placeholder="12"
        />
        <div
          v-if="validate.streetNumber.$error"
          class="text-theme-6 text-red-500 mt-2 text-sm"
        >
          {{ $t('form.validation.requiredField') }}
        </div>
      </div>
    </div>

    <!-- GPS TOGGLE SWITCH -->
    <div class="mt-4 flex items-center justify-end">
      <label for="gps-toggle" class="cursor-pointer mr-2 select-none">
        {{ $t('common.gpsManualEntry') }}
      </label>
      <input
        id="gps-toggle"
        v-model="isManualGps"
        class="show-code form-check-switch mr-0 ml-3"
        type="checkbox"
      />
    </div>

    <!-- Lat/Lon Inputs -->
    <div class="grid grid-cols-2 gap-4 mt-2">
      <!-- Latitude -->
      <div>
        <label class="form-label">{{ $t('address.latitude') }}</label>
        <input
          v-model="validate.latitude.$model"
          type="text"
          :readonly="!isManualGps"
          class="intro-y form-control py-3 px-4 box w-full border"
          :class="{
            'bg-gray-100 cursor-not-allowed text-gray-500': !isManualGps,
            'bg-white': isManualGps,
            'border-theme-6 border-red-500': validate.latitude.$error
          }"
          placeholder="48.000000"
        />
        <div
          v-if="validate.latitude.$error"
          class="text-theme-6 text-red-500 mt-1 text-sm"
        >
          {{ $t('form.validation.requiredField') }}
        </div>
      </div>

      <!-- Longitude -->
      <div>
        <label class="form-label">{{ $t('address.longitude') }}</label>
        <input
          v-model="validate.longitude.$model"
          type="text"
          :readonly="!isManualGps"
          class="intro-y form-control py-3 px-4 box w-full border"
          :class="{
            'bg-gray-100 cursor-not-allowed text-gray-500': !isManualGps,
            'bg-white': isManualGps,
            'border-theme-6 border-red-500': validate.longitude.$error
          }"
          placeholder="19.000000"
        />
        <div
          v-if="validate.longitude.$error"
          class="text-theme-6 text-red-500 mt-1 text-sm"
        >
          {{ $t('form.validation.requiredField') }}
        </div>
      </div>
    </div>

    <!-- MAP CONTAINER -->
    <div class="mt-4 w-full h-[500px] border rounded overflow-hidden relative z-0">
      <ol-map
        :loadTilesWhileAnimating="true"
        :loadTilesWhileInteracting="true"
        style="height: 500px"
        @singleclick="onMapClick"
      >
        <!-- Map View: synced to mapCenter and mapZoom -->
        <ol-view
          ref="view"
          :center="mapCenter"
          :zoom="mapZoom"
          :projection="projection"
        />

        <ol-tile-layer>
          <ol-source-osm />
        </ol-tile-layer>

        <ol-vector-layer v-if="markerCoordinates">
          <ol-source-vector>
            <ol-feature>
              <ol-geom-point :coordinates="markerCoordinates"></ol-geom-point>
              <ol-style>
                <ol-style-icon
                  src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png"
                  :scale="1"
                  :anchor="[0.5, 1]"
                ></ol-style-icon>
              </ol-style>
            </ol-feature>
          </ol-source-vector>
        </ol-vector-layer>
      </ol-map>
      <div class="text-gray-500 text-xs mt-1">
        {{ $t('address.clickToSetPosition') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, computed, onMounted } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, decimal, helpers } from '@vuelidate/validators'
import { fromLonLat, toLonLat } from 'ol/proj'

// --- Props & Emits ---
const props = defineProps({
  modelValue: { type: Object, required: true }
})
const emit = defineEmits(['update:modelValue'])

// --- Local State ---
const localForm = reactive({ ...props.modelValue })
const isManualGps = ref(false)

// Sync up/down
watch(localForm, (newVal) => {
  emit('update:modelValue', newVal)
})
watch(
  () => props.modelValue,
  (newVal) => {
    Object.assign(localForm, newVal)
  },
  { deep: true }
)
const zipValidator = helpers.regex(/^(\s*\d\s*){5}$/)
// --- Validation ---
const rules = {
  street: { required },
  streetNumber: { required },
  city: { required },
  zip: { required, zipFormat: helpers.withMessage('form.validation.zipValidator', zipValidator) },
  latitude: { required, decimal },
  longitude: { required, decimal }
}
const validate = useVuelidate(rules, localForm)
defineExpose({ validate })

// --- OpenLayers Logic ---
const projection = ref('EPSG:3857')
const mapZoom = ref(10)
const mapCenter = ref(fromLonLat([20.02, 49.02]))

const markerCoordinates = computed(() => {
  const lat = parseFloat(localForm.latitude)
  const lon = parseFloat(localForm.longitude)
  if (!isNaN(lat) && !isNaN(lon)) {
    return fromLonLat([lon, lat])
  }
  return null
})

// === NEW: Watcher to Refocus Map when Lat/Lon changes ===
watch([() => localForm.latitude, () => localForm.longitude], ([newLat, newLon]) => {
  const lat = parseFloat(newLat)
  const lon = parseFloat(newLon)

  // Check if we have valid numbers
  if (!isNaN(lat) && !isNaN(lon)) {
    // 1. Update Center
    mapCenter.value = fromLonLat([lon, lat])

    // 2. Zoom in if user was zoomed out too far
    if (mapZoom.value < 14) {
      mapZoom.value = 16
    }
  }
})

onMounted(() => {
  // Initial center if data exists
  if (localForm.latitude && localForm.longitude) {
    const lat = parseFloat(localForm.latitude)
    const lon = parseFloat(localForm.longitude)
    mapCenter.value = fromLonLat([lon, lat])
    mapZoom.value = 16
  }
})

// Handle Map Click
const onMapClick = (event) => {
  const pixelCoords = event.coordinate
  if (!pixelCoords) return

  const coords = toLonLat(pixelCoords)

  // Updating these triggers the 'watch' above, which recenters the map
  localForm.latitude = coords[1].toFixed(6)
  localForm.longitude = coords[0].toFixed(6)

  // Clear validation errors
  validate.value.latitude.$touch()
  validate.value.longitude.$touch()
}

// --- Autocomplete ---
const suggestions = ref([])
const activeField = ref(null)
let debounceTimer = null

const handleInput = (fieldType) => {
  activeField.value = fieldType
  let val = ''
  if (fieldType === 'street') val = localForm.street
  if (fieldType === 'city') val = localForm.city
  if (fieldType === 'postalcode') val = localForm.zip

  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    performSearch(val, fieldType)
  }, 500)
}

const performSearch = async (query, type) => {
  if (!query || query.length < 3) {
    suggestions.value = []
    return
  }

  let q = query
  if (type === 'street') {
    if (localForm.city) q = `${query}, ${localForm.city}`
    else if (localForm.zip) q = `${query}, ${localForm.zip}`
  }
  if (type === 'postalcode' && localForm.city) {
    q = `${query}, ${localForm.city}`
  }

  try {
    const params = new URLSearchParams({
      q: q,
      format: 'json',
      addressdetails: 1,
      countrycodes: 'sk',
      limit: 5
    })
    if (type === 'city') params.append('featuretype', 'city')

    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`)
    const data = await res.json()
    suggestions.value = data
  } catch (error) {
    console.error('Geocoding error:', error)
  }
}

const selectAddress = (item) => {
  const addr = item.address
  if (addr.road || addr.pedestrian) localForm.street = addr.road || addr.pedestrian || ''
  if (addr.house_number) localForm.streetNumber = addr.house_number || ''
  if (addr.city || addr.town || addr.village)
    localForm.city = addr.city || addr.town || addr.village || ''
  if (addr.postcode) localForm.zip = addr.postcode || ''

  if (item.lat && item.lon) {
    // This updates inputs, which triggers the watcher -> centers map
    localForm.latitude = parseFloat(item.lat).toFixed(6)
    localForm.longitude = parseFloat(item.lon).toFixed(6)
  }

  suggestions.value = []
  activeField.value = null
}

window.addEventListener('click', (e) => {
  if (!e.target.closest('.relative')) {
    suggestions.value = []
  }
})
</script>

<style scoped>
.ol-map {
  width: 100%;
  height: 100%;
}
</style>
